import type { InfiniteData, UseSuspenseInfiniteQueryOptions } from '@tanstack/react-query';
import { useSuspenseInfiniteQuery, useSuspenseQuery } from '@tanstack/react-query';
import type { Flow } from '../types';
import queryKeys from './queryKeys';
import type { QueryConfig } from 'lib/react-query';
import type { ListResponse, SortingParams } from 'types/shared';
import { apiClient } from 'lib/api';
import queryString from 'query-string';

export interface SelectFlowsParams {
  has_default_facebook_page?: boolean;
  has_default_creative_text?: boolean;
  apps?: string[];
}

interface GetFlowsParams extends Partial<SortingParams>, SelectFlowsParams {
  name?: string;
  limit?: number;
  offset?: number;
}

export const getFlows = async (
  params?: GetFlowsParams,
  signal?: AbortSignal,
): Promise<ListResponse<Flow>> => {
  const res = await apiClient.get<ListResponse<Flow>>(queryKeys.flows, {
    signal,
    params,
    paramsSerializer: (prms) =>
      queryString.stringify(prms, { arrayFormat: 'comma' }),
  });
  return res.data;
};

interface UseFlowsOptions<TData> {
  config?: QueryConfig<typeof getFlows, TData>;
  params?: GetFlowsParams;
}

export const useFlows = <TData = ListResponse<Flow>>
({
   config,
   params,
 }: UseFlowsOptions<TData> = {}) => {
  return useSuspenseQuery({
    staleTime: 60 * 30 * 1000,
    ...config,
    queryFn: ({ signal }) => getFlows(params, signal),
    queryKey: [queryKeys.flows, params],
  });
};

interface UseInfiniteFlowsOptions {
  config?: Omit<
    UseSuspenseInfiniteQueryOptions<
      ListResponse<Flow>,
      unknown,
      InfiniteData<ListResponse<Flow>>,
      readonly unknown[],
      number
    >,
    'queryFn' | 'queryKey' | 'initialPageParam' | 'getNextPageParam'
  >;
  params?: GetFlowsParams;
}

export const useInfiniteFlows =
  ({
     config,
     params,
   }: UseInfiniteFlowsOptions = {}) => {
    return useSuspenseInfiniteQuery({
      ...config,
      queryFn: ({ pageParam = 1, signal }) =>
        getFlows(
          {
            ...params,
            offset: pageParam,
            limit: params?.limit ?? 100,
          },
          signal,
        ),
      queryKey: [queryKeys.flows, 'infinite', params],
      getNextPageParam: (lastPage, allPages) => {
        const totalItems = lastPage.count;
        const loadedItems = allPages.reduce(
          (acc, page) => acc + page.data.length,
          0,
        );

        if (loadedItems >= totalItems) {
          return undefined;
        }

        return allPages.length + 1;
      },
      initialPageParam: 1,
    });
  };
